import { useQueryClient } from "@tanstack/react-query";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useGqlMutation, Variables } from "@/lib/GraphQLCodegen/fetcher";
import {
  invalidateThreadQueries,
  THREAD_ROW_MUTATION_KEY,
} from "@/app/Threads/utils/invalidateThreadQueries";

/**
 * A change to a thread, and the refetch that carries it into every list showing
 * it. A thread's place in a list is the server's answer to give, so the row
 * only moves once it has answered.
 *
 * @param isRowAction Whether a row should show itself waiting on this. Reading
 * happens by opening a thread rather than by asking for it, so it stays quiet.
 */
export function useThreadMutation<TData, TVariables extends Variables>(
  document: TypedDocumentNode<TData, TVariables>,
  { isRowAction = true }: { isRowAction?: boolean } = {},
) {
  const queryClient = useQueryClient();

  return useGqlMutation(document, {
    mutationKey: isRowAction ? THREAD_ROW_MUTATION_KEY : undefined,
    // Returned, so the mutation stays pending until the lists have caught up.
    // That is what holds the row's spinner up until the row itself changes.
    onSettled: () => invalidateThreadQueries(queryClient),
    // Both of these are here to keep the client-wide defaults off this mutation:
    // its onSuccess invalidates every query in the app, which for a paginated
    // list means refetching every loaded page of both status tabs, and its
    // onError raises a second toast over the one the caller gives.
    onSuccess: () => {},
    onError: () => {},
  });
}
